// Subtask Classification Dialogue Blueprint
// This file defines the static statechart for processing individual subtasks
// within a well-defined task. It handles leaf validation, variant selection,
// children selection, and amount collection for each subtask.

export type SubtaskDialogueState = 
  | 'leaf_validation'           // Step 1: Check if subtask is a leaf, has variants, or children
  | 'variant_selection'          // Step 2a: User selects a variant if subtask has variants
  | 'option_selection'           // Step 2b: User selects an option (child) if subtask has children
  | 'amount_collection'          // Step 3: User provides amount based on billable unit
  | 'subtask_completed'          // Terminal: Subtask processing complete
  | 'subtask_aborted'            // Terminal: Subtask processing aborted

// Typed triggers for compiler-checked transitions
export type SubtaskTrigger = 
  | 'VALIDATE_LEAF'              // System trigger to validate if subtask is leaf
  | 'HAS_VARIANTS'               // Leaf validation result: task has variants → go to variant selection
  | 'IS_LEAF'                    // Leaf validation result: task is a leaf → go to amount collection
  | 'HAS_CHILDREN'               // Leaf validation result: task has children → go to option selection (go deeper)
  | 'VARIANT_SELECTED'           // User selected a variant
  | 'OPTION_SELECTED'            // User selected an option (child) (recursively validate)
  | 'AMOUNT_PROVIDED'            // User provided amount
  | 'CONFIRM'                    // User confirms action
  | 'ABORT'                      // User wants to abort
  | 'NEXT_SUBTASK'               // Move to next subtask
  | 'SUBTASK_COMPLETED'          // Parent-facing terminal trigger
  | 'SUBTASK_ABORTED'            // Parent-facing terminal trigger

// State metadata interface
export interface SubtaskStateMetadata {
  requiredSlots?: string[];
  entryEffectKey?: string;
  isTerminal?: boolean;
  entryActionKeys?: string[];
}

// Pure static statechart - no actions or I/O
// The transitions define the flow:
// 1. When an option is selected from option_selection → validate it (go to leaf_validation)
// 2. Based on leaf_validation result:
//    - If has variants → variant_selection
//    - If is a leaf → amount_collection
//    - If has children → option_selection (go deeper, recursive - the selected option becomes the new task)
export const SUBTASK_STATE_TRANSITIONS: Record<SubtaskDialogueState, Partial<Record<SubtaskTrigger, SubtaskDialogueState>>> = {
  
  leaf_validation: {
    'HAS_VARIANTS': 'variant_selection',      // Priority 1: Has variants → go to variant selection
    'IS_LEAF': 'amount_collection',           // Priority 2: Is a leaf → go to amount collection
    'HAS_CHILDREN': 'option_selection',       // Priority 3: Has children → go deeper (recursive)
    'ABORT': 'subtask_aborted'
  },
  
  variant_selection: {
    'VARIANT_SELECTED': 'amount_collection',  // After variant selected → go to amount collection
    'ABORT': 'subtask_aborted'
  },
  
  option_selection: {
    'OPTION_SELECTED': 'leaf_validation',     // When option selected → validate it first
    // After validation, transitions from leaf_validation will determine next state:
    // - HAS_CHILDREN → back to option_selection (recursive, with updated task)
    // - HAS_VARIANTS → variant_selection
    // - IS_LEAF → amount_collection
    'ABORT': 'subtask_aborted'
  },
  
  amount_collection: {
    'AMOUNT_PROVIDED': 'subtask_completed',   // After amount provided → subtask completed
    'ABORT': 'subtask_aborted'
  },
  
  subtask_completed: {
    // Terminal state - no transitions
  },
  
  subtask_aborted: {
    // Terminal state - no transitions
  }
};

// State metadata
export const SUBTASK_STATE_METADATA: Record<SubtaskDialogueState, SubtaskStateMetadata> = {
  leaf_validation: {
    requiredSlots: [],
    entryActionKeys: ['validate_subtask_leaf']
  },
  variant_selection: {
    requiredSlots: ['variant'],
    entryActionKeys: ['show_variant_options']
  },
  option_selection: {
    requiredSlots: ['option'],
    entryActionKeys: ['show_children_options']
  },
  amount_collection: {
    requiredSlots: ['amount'],
    entryActionKeys: ['ask_amount']
  },
  subtask_completed: {
    isTerminal: true
  },
  subtask_aborted: {
    isTerminal: true
  }
};

// Pure helper functions - no side effects
export function getNextSubtaskState(
  currentState: SubtaskDialogueState,
  trigger: SubtaskTrigger
): SubtaskDialogueState | null {
  const transitions = SUBTASK_STATE_TRANSITIONS[currentState];
  return transitions[trigger] || null;
}

export function isValidSubtaskTransition(
  currentState: SubtaskDialogueState,
  trigger: SubtaskTrigger
): boolean {
  const nextState = getNextSubtaskState(currentState, trigger);
  return nextState !== null && nextState !== currentState;
}

export function getSubtaskStateMetadata(
  state: SubtaskDialogueState
): SubtaskStateMetadata {
  return SUBTASK_STATE_METADATA[state];
}

